import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, Send, AlertTriangle, CheckCircle2, Clock, User, Bot, Shield } from "lucide-react";

const Support = () => {
  const { token, user } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  
  const [newTicketForm, setNewTicketForm] = useState({
    subject: "",
    category: "general",
    message: "",
    priority: "medium"
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (selectedTicket) {
      fetchMessages(selectedTicket.id);
    }
  }, [selectedTicket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/support/my-tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data.tickets);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    }
  };

  const fetchMessages = async (ticketId) => {
    try {
      const response = await axios.get(`${API}/support/ticket/${ticketId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const createTicket = async () => {
    if (!newTicketForm.subject || !newTicketForm.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/support/ticket`, newTicketForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Ticket created successfully!");
      setShowNewTicket(false);
      setNewTicketForm({ subject: "", category: "general", message: "", priority: "medium" });
      fetchTickets();
      
      // Auto-select the new ticket
      setSelectedTicket(response.data.ticket);
      
    } catch (error) {
      toast.error("Failed to create ticket");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await axios.post(`${API}/support/chat`, {
        ticket_id: selectedTicket.id,
        message: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewMessage("");
      fetchMessages(selectedTicket.id);
      fetchTickets(); // Update ticket list
    } catch (error) {
      toast.error("Failed to send message");
      console.error(error);
    }
  };

  const escalateTicket = async () => {
    try {
      await axios.post(`${API}/support/escalate`, {
        ticket_id: selectedTicket.id,
        reason: "User requested human support"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Ticket escalated to human support");
      fetchTickets();
      fetchMessages(selectedTicket.id);
    } catch (error) {
      toast.error("Failed to escalate ticket");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { color: "bg-blue-100 text-blue-700", label: "Open" },
      ai_handling: { color: "bg-purple-100 text-purple-700", label: "AI Handling" },
      escalated: { color: "bg-orange-100 text-orange-700", label: "Escalated" },
      in_progress: { color: "bg-yellow-100 text-yellow-700", label: "In Progress" },
      resolved: { color: "bg-green-100 text-green-700", label: "Resolved" },
      closed: { color: "bg-gray-100 text-gray-700", label: "Closed" }
    };
    
    const config = statusConfig[status] || statusConfig.open;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getSenderIcon = (senderType) => {
    if (senderType === "user") return <User className="w-5 h-5" />;
    if (senderType === "ai") return <Bot className="w-5 h-5" />;
    if (senderType === "admin") return <Shield className="w-5 h-5" />;
    return <MessageCircle className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <MessageCircle className="w-8 h-8 text-primary" />
                Support Center
              </h1>
              <p className="text-gray-600 mt-1">Get help from our AI assistant or support team</p>
            </div>
            <Button onClick={() => setShowNewTicket(true)} size="lg" className="shadow-md">
              <MessageCircle className="w-4 h-4 mr-2" />
              New Ticket
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tickets List */}
          <div className="lg:col-span-1">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle>My Tickets</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[600px] overflow-y-auto">
                  {tickets.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      <MessageCircle className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No tickets yet</p>
                      <p className="text-sm">Create a ticket to get started</p>
                    </div>
                  ) : (
                    tickets.map((ticket) => (
                      <div
                        key={ticket.id}
                        onClick={() => setSelectedTicket(ticket)}
                        className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedTicket?.id === ticket.id ? "bg-primary/5 border-l-4 border-l-primary" : ""
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-sm line-clamp-1">{ticket.subject}</h3>
                          {getStatusBadge(ticket.status)}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <Clock className="w-3 h-3" />
                          {new Date(ticket.created_at).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {ticket.category}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            Level {ticket.level}
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Chat Area */}
          <div className="lg:col-span-2">
            {showNewTicket ? (
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle>Create New Ticket</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Subject *</label>
                    <Input
                      value={newTicketForm.subject}
                      onChange={(e) => setNewTicketForm({...newTicketForm, subject: e.target.value})}
                      placeholder="Brief description of your issue"
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Category *</label>
                      <Select
                        value={newTicketForm.category}
                        onValueChange={(value) => setNewTicketForm({...newTicketForm, category: value})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="account">Account</SelectItem>
                          <SelectItem value="technical">Technical</SelectItem>
                          <SelectItem value="billing">Billing</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Priority</label>
                      <Select
                        value={newTicketForm.priority}
                        onValueChange={(value) => setNewTicketForm({...newTicketForm, priority: value})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Message *</label>
                    <Textarea
                      value={newTicketForm.message}
                      onChange={(e) => setNewTicketForm({...newTicketForm, message: e.target.value})}
                      placeholder="Describe your issue in detail..."
                      rows={4}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={createTicket} disabled={loading} className="flex-1">
                      {loading ? "Creating..." : "Create Ticket"}
                    </Button>
                    <Button variant="outline" onClick={() => setShowNewTicket(false)}>
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : selectedTicket ? (
              <Card className="border-none shadow-lg h-[700px] flex flex-col">
                <CardHeader className="border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{selectedTicket.subject}</CardTitle>
                      <div className="flex items-center gap-2 mt-2">
                        {getStatusBadge(selectedTicket.status)}
                        <Badge variant="outline">Level {selectedTicket.level}</Badge>
                      </div>
                    </div>
                    {selectedTicket.level === 1 && selectedTicket.status !== "escalated" && (
                      <Button variant="outline" size="sm" onClick={escalateTicket}>
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Request Human Support
                      </Button>
                    )}
                  </div>
                </CardHeader>
                
                {/* Messages */}
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex gap-3 ${msg.sender_type === "user" ? "flex-row-reverse" : "flex-row"}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.sender_type === "user" ? "bg-primary/10 text-primary" :
                        msg.sender_type === "ai" ? "bg-purple-100 text-purple-600" :
                        "bg-green-100 text-green-600"
                      }`}>
                        {getSenderIcon(msg.sender_type)}
                      </div>
                      <div className={`flex-1 max-w-[80%] ${msg.sender_type === "user" ? "text-right" : ""}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium">{msg.sender_name}</span>
                          <span className="text-xs text-gray-500">
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className={`p-3 rounded-lg ${
                          msg.sender_type === "user" ? "bg-primary text-white" :
                          msg.sender_type === "ai" ? "bg-purple-50 text-gray-800" :
                          "bg-green-50 text-gray-800"
                        }`}>
                          <p className="whitespace-pre-wrap">{msg.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </CardContent>
                
                {/* Input */}
                {selectedTicket.status !== "closed" && selectedTicket.status !== "resolved" && (
                  <div className="border-t p-4">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                        placeholder="Type your message..."
                        className="flex-1"
                      />
                      <Button onClick={sendMessage}>
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ) : (
              <Card className="border-none shadow-lg h-[700px] flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">Select a ticket to view conversation</p>
                  <p className="text-sm">or create a new ticket to get started</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Support;
